import log from "loglevel";

import { BrandTheme } from "./brands";
import {
    ConfigState,
    CsatConfig,
    CsatScale,
    CSAT_SCALES,
    LauncherPosition,
    PrechatCard,
    PrechatConfig,
    PrechatField
} from "./store/definitions";
import { reportClientError } from "./utils/reportClientError";
import { descreverErro } from "./utils/descreverErro";

/*
 * Config de marca vinda do dashboard em runtime (cor + logo + posição + borda + telas pré-chat), por marca.
 * Permite trocar tema/logo/posição/pré-chat pelo painel sem rebuild. Best-effort: se a env não estiver
 * setada ou o fetch falhar, retorna {} e o widget usa os defaults de build (brands.ts) e o CSS atual.
 *
 * Contrato (idêntico ao dashboard): GET {REACT_APP_BRAND_CONFIG_URL}?brand=<slug>
 * → { theme?, launcherLogo?, launcherPosition?, borderRadius?, headerTitle?, prechat? }
 */
interface RemoteBrandConfig {
    theme?: Partial<BrandTheme>;
    launcherLogo?: string;
    launcherPosition?: LauncherPosition;
    borderRadius?: number;
    headerTitle?: string;
    prechat?: PrechatConfig;
    /* Payload externo — shape validado por normalizeCsat (pode driftar); por isso `unknown`. */
    csat?: unknown;
    /* Quantas promoções estão no ar. Vem no LOAD porque o contador do card aparece na tela inicial. */
    promocoes?: { vigentes?: unknown };
}

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/* Mantém só os itens de array que passam no predicado E têm um identificador único (dedup, 1º vence). */
function keepUnique<T>(items: unknown[], isValid: (item: unknown) => item is T, idOf: (item: T) => string): T[] {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const item of items) {
        if (!isValid(item)) continue;
        const id = idOf(item);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(item);
    }
    return out;
}

/*
 * Blindagem do `prechat` vindo de config EXTERNA (dashboard, contrato pode driftar). Descarta o que
 * for malformado e deduplica ids/keys, garantindo que o widget nunca lance no render por um payload
 * ruim (mesmo espírito best-effort do notifyWelcome/alert). Shape inválido de uma sub-chave = omitida
 * → o componente cai no default daquela seção. Nunca lança.
 */
export function normalizePrechat(raw: unknown): PrechatConfig | undefined {
    if (!isObject(raw)) return undefined;

    const out: PrechatConfig = {};
    if (isObject(raw.welcome)) out.welcome = raw.welcome as PrechatConfig["welcome"];
    if (isObject(raw.loading)) out.loading = raw.loading as PrechatConfig["loading"];

    if (Array.isArray(raw.cards)) {
        out.cards = keepUnique<PrechatCard>(
            raw.cards,
            (c): c is PrechatCard => isObject(c) && typeof c.id === "string",
            (c) => c.id
        );
    }

    if (isObject(raw.form) && Array.isArray(raw.form.fields)) {
        out.form = {
            fields: keepUnique<PrechatField>(
                raw.form.fields,
                (f): f is PrechatField => isObject(f) && typeof f.key === "string",
                (f) => f.key
            )
        };
    }

    return out;
}

const CSAT_HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const CSAT_DEFAULTS = {
    title: "Como foi seu atendimento?",
    thankYouText: "Obrigado pela sua avaliação!",
    commentLabel: "Deixe um comentário (opcional)",
    primaryColor: "var(--primary-button)"
};

/*
 * Blindagem do `csat` vindo do brand-config (config EXTERNA, contrato pode driftar). Mesmo espírito
 * best-effort do normalizePrechat: NUNCA lança. "omitido / enabled != true" = CSAT desligado (retorna
 * undefined → o card nem renderiza). Sub-campos ausentes/malformados caem em default seguro pt-BR;
 * `scale` fora do enum → "stars"; `primaryColor` só passa se for #hex (senão usa a var da marca, S7).
 */
export function normalizeCsat(raw: unknown): CsatConfig | undefined {
    if (!isObject(raw) || raw.enabled !== true) return undefined;

    const appearance = isObject(raw.appearance) ? raw.appearance : {};
    const comment = isObject(appearance.comment) ? appearance.comment : {};
    const windowCfg = isObject(raw.window) ? raw.window : {};

    const scale = CSAT_SCALES.includes(appearance.scale as CsatScale) ? (appearance.scale as CsatScale) : "stars";
    const primaryColor =
        typeof appearance.primaryColor === "string" && CSAT_HEX_COLOR.test(appearance.primaryColor)
            ? appearance.primaryColor
            : CSAT_DEFAULTS.primaryColor;

    return {
        enabled: true,
        appearance: {
            title: typeof appearance.title === "string" ? appearance.title : CSAT_DEFAULTS.title,
            /*
             * Textos da faixa do atendimento: OMITIDOS quando ausentes ou malformados, e não
             * substituídos por um default daqui. O default dessa variante mora no `CsatCard`, porque a
             * superfície é nova e o dashboard ainda não a conhece — carimbar um default nesta camada
             * esconderia a diferença entre "a marca não mandou" e "a marca mandou isto".
             *
             * ⚠️ Esta função é ALLOWLIST: ela reconstrói o objeto campo a campo. Campo novo do contrato
             * que não for copiado aqui nunca chega ao componente, por mais que o dashboard o mande — e
             * teste de componente não pega, porque ele injeta o objeto já normalizado.
             */
            ...(typeof appearance.titleDuringChat === "string"
                ? { titleDuringChat: appearance.titleDuringChat }
                : {}),
            thankYouText:
                typeof appearance.thankYouText === "string" ? appearance.thankYouText : CSAT_DEFAULTS.thankYouText,
            scale,
            comment: {
                enabled: comment.enabled === true,
                label: typeof comment.label === "string" ? comment.label : CSAT_DEFAULTS.commentLabel,
                ...(typeof comment.labelDuringChat === "string"
                    ? { labelDuringChat: comment.labelDuringChat }
                    : {})
            },
            primaryColor
        },
        window: { allowEdit: windowCfg.allowEdit === true }
    };
}

/*
 * Traduz a config remota nos overrides do ConfigReducer (dispatch em index.tsx). Cada chave é OPCIONAL:
 * ausente = não mexe no estado (mantém o default de build). Função pura → testável isolada.
 * NÃO inclui `theme`/`launcherPosition` como efeitos colaterais (CSS vars) — só o que vai pro store.
 */
export function remoteConfigToOverrides(remote: RemoteBrandConfig): ConfigState {
    const overrides: ConfigState = {};
    if (remote.launcherLogo) overrides.launcherLogo = remote.launcherLogo;
    if (remote.launcherPosition) overrides.launcherPosition = remote.launcherPosition;
    if (typeof remote.borderRadius === "number") overrides.borderRadius = remote.borderRadius;
    if (remote.headerTitle) overrides.headerTitle = remote.headerTitle;
    // Blinda o prechat (config externa): shape validado + ids/keys deduplicados antes de ir pro store.
    const prechat = normalizePrechat(remote.prechat);
    if (prechat) overrides.prechat = prechat;
    // Blinda o csat (config externa): shape validado; desligado/malformado ⇒ undefined ⇒ omitido.
    const csat = normalizeCsat(remote.csat);
    if (csat) overrides.csat = csat;
    /*
     * ZERO é valor válido e tem de passar: o contador aparece mesmo valendo zero (padrão
     * "Avisos 0"). Um teste truthy apagaria justamente o caso que motivou o campo.
     */
    const vigentes = remote.promocoes?.vigentes;
    if (typeof vigentes === "number" && Number.isFinite(vigentes) && vigentes >= 0) {
        overrides.promocoesVigentes = Math.trunc(vigentes);
    }
    return overrides;
}

export async function getBrandConfig(brand: string): Promise<RemoteBrandConfig> {
    const baseUrl = (process.env.REACT_APP_BRAND_CONFIG_URL || "").trim();
    if (!baseUrl) {
        return {};
    }

    try {
        const separator = baseUrl.includes("?") ? "&" : "?";
        const response = await fetch(`${baseUrl}${separator}brand=${encodeURIComponent(brand)}`, {
            method: "GET",
            headers: { Accept: "application/json" }
        });

        if (!response.ok) {
            // Saía por aqui sem NENHUM registro — mais silencioso ainda que a exceção abaixo.
            reportClientError("brand_config_failed", { reason: `HTTP ${response.status}` });
            return {};
        }

        return (await response.json()) as RemoteBrandConfig;
    } catch (e) {
        /*
         * 🔒 `descreverErro`, não o objeto. O widget é injetado DIRETO no DOM do site da marca, sem
         * iframe — qualquer script daquela página lê o console. Publicar o `Error` ali entrega
         * `message` e `stack`, e mensagem de erro carrega dado do apostador o tempo todo (nome de
         * arquivo anexado, trecho de input). Último ponto da base que gravava o objeto cru.
         */
        log.warn(`Couldn't fetch remote brand config; using build-time defaults: ${descreverErro(e)}`);
        /*
         * O fallback para o default de build é DE PROPÓSITO e continua igual: o apostador é atendido
         * do mesmo jeito. O que muda é a operação saber. Sem isto, o endpoint podia ficar fora
         * indefinidamente com todas as marcas no default e o único sintoma seria alguém reparar na
         * cor errada. Degradação: conta no log, fora do WhatsApp.
         */
        reportClientError("brand_config_failed", { reason: descreverErro(e) });
        return {};
    }
}
